import { Checkbox, CircularProgress, FormControlLabel, Typography } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { useAppSelector } from "@/redux/hooks";
import ResponsiveSelect from "../../../components/ui/ResponsiveSelect";
import useContactsForAppAndSite from "../../../hooks/useContactsForAppAndSite";
import { formatShortMonthDate } from "../../../utils/dateTimeUtils";
import { useActionDetails } from "../../contexts/ActionContext";
import ActionStatusChip from "../ActionStatusChip";
import { KeyboardDatePicker } from "@/compat/pickers";

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: theme.spacing(1),
    },
    twoColumn: {
        display: 'grid',
        width: '100%',
        gridTemplateColumns: 'min-content auto',
        rowGap: theme.spacing(1.5),
        columnGap: theme.spacing(8),
        alignItems: 'center',
    },
    keyText: {
        color: '#716994',
    },
    userName: {
        textDecoration: 'underline',
        color: '#5c45c7',
        maxWidth: 'max-content',
        '&:hover': {
            cursor: 'pointer',
        },
    },
    userSelect: {
        display: 'flex',
        alignItems: 'center',
        columnGap: theme.spacing(1),
    },
    radioContainer: {
        fontSize: '0.875rem',
        display: 'flex',
        alignItems: 'center',
        width: 'max-content',
    },
}));

export default function ActionDetails() {
    const classes = useStyles();    

    const {
        action,
        assignedEmployeeExternalId,
        setAssignedEmployeeExternalId,
        isEditAllowed,
        isComplete,
        setIsComplete,
        dueDate,
        setDueDate
    } = useActionDetails()
    
    const { activeAppId, appSiteIds, selectedSite } = useAppSelector(
        (state) => state.account
    );

    const siteUsers = useContactsForAppAndSite({
        selectedSiteId: action.data?.siteExternalId
    });

    const onAssignedToChange = (e) => {
        setAssignedEmployeeExternalId(e.target.value);        
    };

    const onDueDateChange = (date) => {
        if (date)
            setDueDate(date);        
    };

    const UserName = ({ userId }) => {
        if (siteUsers.isLoading || action.isLoading)
            return <CircularProgress size={20} />;

        if (siteUsers.isError || action.isError)
            return <Typography>Unknown</Typography>;

        let fullName = 'Not Set'

        if (userId)
            fullName = `${siteUsers?.data[userId]?.firstName} ${siteUsers?.data[userId]?.lastName}`;

        return (
            <Typography>
                {fullName}
            </Typography>
        )
    };

    const keyText = (text) => <Typography className={classes.keyText} variant="subtitle2">{text}</Typography>

    return (
        <div className={classes.container}>
            <div className={classes.twoColumn}>
                {action.data?.id && (
                    <>
                        {keyText('Reference')}
                        <Typography>{action.data.id}</Typography>
                    </>
                )}
                {action.data?.description && (
                    <>
                        {keyText('Observation')}
                        <Typography>
                            {action.data.description}
                        </Typography>
                    </>
                )}
                {action.data?.recommendation && (
                    <>
                        {keyText('Recommendation')}
                        <Typography>
                            {action.data.recommendation}
                        </Typography>
                    </>
                )}
                {action.data?.reportSection && (
                    <>
                        {keyText('Section')}
                        <Typography>
                            {action.data.reportSection}
                        </Typography>
                    </>
                )}
                {action.data?.reportSubSection && (
                    <>
                        {keyText('Sub-Section')}
                        <Typography>
                            {action.data.reportSubSection}
                        </Typography>
                    </>
                )}
                {keyText('Status')}
                <ActionStatusChip
                    status={action.data?.status}
                    statusName={action.data?.statusName}
                />
                {keyText('Due')}
                {
                    isEditAllowed
                        ? <div className={classes.userSelect}>
                            <KeyboardDatePicker
                                format="dd/MM/yyyy"
                                variant='inline'
                                inputVariant="outlined"
                                label="Due Date"
                                value={dueDate}
                                onChange={onDueDateChange}                               
                            />
                         </div>
                        : <Typography                          
                        >
                            {dueDate && formatShortMonthDate(dueDate)}
                        </Typography>
                }
                {keyText('Responsible')}
                {isEditAllowed ? (
                    <div className={classes.userSelect}>
                        <ResponsiveSelect
                            options={Object.values(siteUsers?.data ?? {}).sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""))}
                            optionLabelFunction={(x) => `${x.firstName} ${x.lastName}`}
                            optionValueKey={"externalId"}
                            value={assignedEmployeeExternalId}
                            onChange={onAssignedToChange}
                            margin="dense"
                            style={{ margin: 0, minWidth: '110px' }}
                        />                      
                    </div>
                ) : (
                    <div>
                        <UserName userId={assignedEmployeeExternalId} />
                    </div>
                )}
                {keyText('Created By')}
                {action.data?.createdByEmployeeExternalId ? (
                    <UserName userId={action.data?.createdByEmployeeExternalId} />
                ) : (
                    "N/A"
                )}
                {action.data?.templateName && keyText('Form Origin')}
                {
                    action.data?.templateName &&
                    <Typography>
                        {action.data.templateName}
                    </Typography>
                }
                {action.data?.auditId && keyText('Audit Id')}
                {
                    action.data?.auditId &&
                    <Typography>
                        {action.data.auditId}
                    </Typography>
                }
                {action.data?.incidentId && keyText('Incident Id')}
                {
                    action.data?.incidentId &&
                    <Typography>
                        {action.data.incidentId}
                    </Typography>
                }
                {keyText('Stage')}
                <div>
                    <FormControlLabel
                        className={classes.radioContainer}
                        value={isComplete}
                        disabled={!isEditAllowed}
                        control={
                            <Checkbox
                                checked={isComplete}
                                onChange={() => setIsComplete(!isComplete)}
                            />
                        }
                        label={isComplete ? 'Complete' : 'Incomplete'}
                    />
                </div>
            </div>
        </div>
    )
}